import React, { useState, useEffect } from 'react';
import { Shuffle, Download, Palette, Zap, RefreshCw, Heart, Star, Trash2, Code, FileText, Layers, X, Copy, Contrast, Accessibility, Lightbulb, ImageIcon } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import { ColorPaletteDisplay } from './components/ColorPaletteDisplay';
import { ComponentShowcase } from './components/ComponentShowcase';
import { ModelSelector } from './components/ModelSelector';
import { ColorHarmonySelector } from './components/ColorHarmonySelector';
import { AccessibilityChecker } from './components/AccessibilityChecker';
import { ColorInsights } from './components/ColorInsights';
import { ImageColorExtractor } from './components/ImageColorExtractor';
import { 
  generateColormindPalette, 
  generateWithLockedColors,
  generateHarmoniousPalette, 
  hslToHex,
  hexToRgb 
} from './utils/colorGenerator';
import { 
  initDatabase, 
  addFavoritePalette, 
  removeFavoritePalette, 
  getFavoritePalettes,
  FavoritePalette 
} from './utils/database';
import { ColorPalette } from './types/color';

function App() {
  const [palette, setPalette] = useState<ColorPalette>({ colors: [], names: [] });
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedModel, setSelectedModel] = useState('default');
  const [selectedHarmony, setSelectedHarmony] = useState('analogous');
  const [lockedColors, setLockedColors] = useState<boolean[]>([false, false, false, false, false]);
  const [useAPI, setUseAPI] = useState(true);
  const [activeTab, setActiveTab] = useState<'generator' | 'favorites' | 'insights' | 'extractor'>('generator');
  const [favoritePalettes, setFavoritePalettes] = useState<FavoritePalette[]>([]);
  const [showComponentPreview, setShowComponentPreview] = useState(true);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showAccessibilityPanel, setShowAccessibilityPanel] = useState(false);
  const [exportingPalette, setExportingPalette] = useState<FavoritePalette | null>(null);

  // Initialize database on app start
  useEffect(() => {
    initDatabase();
    loadFavoritePalettes();
  }, []);

  const loadFavoritePalettes = () => {
    setFavoritePalettes(getFavoritePalettes());
  };

  const generateNewPalette = async () => {
    setIsGenerating(true);
    
    try {
      let newPalette: ColorPalette;
      
      if (useAPI) {
        // Check if any colors are locked
        const hasLockedColors = lockedColors.some(locked => locked);
        
        if (hasLockedColors) {
          // Create input array with locked colors
          const lockedInput = palette.colors.map((color, index) => {
            if (lockedColors[index]) {
              const hex = hslToHex(color);
              return hexToRgb(hex);
            }
            return null;
          });
          
          newPalette = await generateWithLockedColors(lockedInput, selectedModel);
          toast.success('Generated palette with locked colors!', {
            icon: '🔒',
            duration: 2000,
          });
        } else {
          newPalette = await generateColormindPalette(selectedModel);
          toast.success(`Generated ${selectedModel.replace(/_/g, ' ')} palette!`, {
            icon: '🎨',
            duration: 2000,
          });
        }
      } else {
        // Use selected harmony for local generation
        newPalette = generateHarmoniousPalette(selectedHarmony);
        toast.success(`Generated ${selectedHarmony.replace(/_/g, ' ')} harmony!`, {
          icon: '🌈',
          duration: 2000,
        });
      }
      
      setPalette(newPalette);
    } catch (error) {
      console.error('Failed to generate palette:', error);
      toast.error('Failed to generate palette, using fallback', {
        icon: '⚠️',
        duration: 3000,
      });
      // Fallback to local generation with selected harmony
      const fallbackPalette = generateHarmoniousPalette(selectedHarmony);
      setPalette(fallbackPalette);
    }
    
    setIsGenerating(false);
  };

  const handleColorLock = (index: number, locked: boolean) => {
    const newLockedColors = [...lockedColors];
    newLockedColors[index] = locked;
    setLockedColors(newLockedColors);
    
    toast(locked ? 'Color locked!' : 'Color unlocked!', {
      icon: locked ? '🔒' : '🔓',
      duration: 1500,
    });
  };

  const handlePaletteExtracted = (extractedPalette: ColorPalette) => {
    setPalette(extractedPalette);
    setActiveTab('generator');
    // Reset locks when new palette is extracted
    setLockedColors([false, false, false, false, false]);
  };

  const openExportModal = (paletteToExport?: FavoritePalette) => {
    setExportingPalette(paletteToExport || null);
    setShowExportModal(true);
  };

  const exportPalette = (format: string) => {
    // Use exporting palette or current palette
    const colorsToExport = exportingPalette ? exportingPalette.colors : palette.colors.map(color => hslToHex(color));
    const namesToExport = exportingPalette ? ['Primary', 'Secondary', 'Accent', 'Dark', 'Darkest'] : palette.names;
    const modelToExport = exportingPalette ? exportingPalette.model : (useAPI ? selectedModel : selectedHarmony);
    
    let exportData: any;
    let filename: string;
    let mimeType: string;

    switch (format) {
      case 'json':
        exportData = {
          colors: colorsToExport,
          names: namesToExport,
          model: modelToExport,
          lockedColors: exportingPalette ? [] : lockedColors,
          generated: new Date().toISOString()
        };
        filename = `nocrenk-palette-${modelToExport || 'custom'}.json`;
        mimeType = 'application/json';
        break;

      case 'css':
        const cssVars = colorsToExport.map((color, index) => 
          `  --color-${namesToExport[index].toLowerCase()}: ${color};`
        ).join('\n');
        exportData = `:root {\n${cssVars}\n}\n\n/* Generated by NocRenk - ${new Date().toLocaleDateString()} */`;
        filename = `nocrenk-palette-${modelToExport || 'custom'}.css`;
        mimeType = 'text/css';
        break;

      case 'scss':
        const scssVars = colorsToExport.map((color, index) => 
          `$color-${namesToExport[index].toLowerCase()}: ${color};`
        ).join('\n');
        exportData = `${scssVars}\n\n// Generated by NocRenk - ${new Date().toLocaleDateString()}`;
        filename = `nocrenk-palette-${modelToExport || 'custom'}.scss`;
        mimeType = 'text/scss';
        break;

      case 'tailwind':
        const tailwindColors = colorsToExport.reduce((acc, color, index) => {
          acc[namesToExport[index].toLowerCase()] = color;
          return acc;
        }, {} as Record<string, string>);
        exportData = `module.exports = {
  theme: {
    extend: {
      colors: ${JSON.stringify(tailwindColors, null, 8)}
    }
  }
}

// Generated by NocRenk - ${new Date().toLocaleDateString()}`;
        filename = `nocrenk-palette-${modelToExport || 'custom'}.tailwind.js`;
        mimeType = 'text/javascript';
        break;

      case 'figma':
        const figmaTokens = {
          colors: colorsToExport.reduce((acc, color, index) => {
            acc[namesToExport[index]] = {
              value: color,
              type: "color"
            };
            return acc;
          }, {} as Record<string, any>)
        };
        exportData = JSON.stringify(figmaTokens, null, 2);
        filename = `nocrenk-palette-${modelToExport || 'custom'}.figma.json`;
        mimeType = 'application/json';
        break;

      case 'adobe':
        const adobeSwatches = colorsToExport.map((color, index) => ({
          name: namesToExport[index],
          color: color,
          type: "PROCESS"
        }));
        exportData = JSON.stringify({ swatches: adobeSwatches }, null, 2);
        filename = `nocrenk-palette-${modelToExport || 'custom'}.adobe.json`;
        mimeType = 'application/json';
        break;

      default:
        return;
    }
    
    const blob = new Blob([typeof exportData === 'string' ? exportData : JSON.stringify(exportData, null, 2)], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setShowExportModal(false);
    setExportingPalette(null);
    
    toast.success(`Exported as ${format.toUpperCase()}!`, {
      icon: '📁',
      duration: 2000,
    });
  };

  const savePaletteToFavorites = () => {
    if (palette.colors.length === 0) return;
    
    const hexColors = palette.colors.map(color => hslToHex(color));
    const paletteName = useAPI 
      ? `${selectedModel.replace(/_/g, ' ')} Palette`
      : `${selectedHarmony.replace(/_/g, ' ')} Harmony`;
    
    const success = addFavoritePalette(paletteName, hexColors, useAPI ? selectedModel : selectedHarmony);
    if (success) {
      loadFavoritePalettes();
      toast.success('Palette saved to favorites!', {
        icon: '💖',
        duration: 2000,
      });
    } else {
      toast.error('Failed to save palette', {
        icon: '❌',
        duration: 2000,
      });
    }
  };

  const loadFavoritePalette = (favPalette: FavoritePalette) => {
    const hslColors = favPalette.colors.map(hex => {
      const rgb = hexToRgb(hex);
      if (!rgb) return 'hsl(0, 0%, 50%)';
      return rgbToHsl(rgb[0], rgb[1], rgb[2]);
    });
    
    setPalette({
      colors: hslColors,
      names: ['Primary', 'Secondary', 'Accent', 'Dark', 'Darkest']
    });
    
    // Determine if this was an API model or harmony
    const isApiModel = ['default', 'ui', 'makoto_shinkai', 'metroid_fusion', 'akira', 'flower_photography', 'sunset', 'vintage', 'modern_art', 'cyberpunk', 'forest', 'ocean', 'desert', 'neon', 'pastel', 'monochrome', 'earth_tones', 'jewel_tones', 'autumn', 'spring', 'winter', 'summer', 'corporate', 'creative', 'minimal', 'bold', 'soft', 'dramatic'].includes(favPalette.model || '');
    
    if (isApiModel) {
      setUseAPI(true);
      setSelectedModel(favPalette.model || 'default');
    } else {
      setUseAPI(false);
      setSelectedHarmony(favPalette.model || 'analogous');
    }
    
    setActiveTab('generator');
    toast.success('Palette loaded!', {
      icon: '🎨',
      duration: 2000,
    });
  };

  const deleteFavoritePalette = (id: number) => {
    removeFavoritePalette(id);
    loadFavoritePalettes();
    toast.success('Palette removed from favorites', {
      icon: '🗑️',
      duration: 2000,
    });
  };

  const resetLocks = () => {
    setLockedColors([false, false, false, false, false]);
    toast.success('All locks reset!', {
      icon: '🔓',
      duration: 1500,
    });
  };

  const copyPaletteToClipboard = () => {
    const hexColors = palette.colors.map(color => hslToHex(color));
    const paletteText = hexColors.join(', ');
    navigator.clipboard.writeText(paletteText);
    toast.success('Palette copied to clipboard!', {
      icon: '📋',
      duration: 2000,
    });
  };

  // Copy individual hex code to clipboard
  const copyHexToClipboard = (hexColor: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    navigator.clipboard.writeText(hexColor);
    toast.success(`${hexColor} copied!`, {
      icon: '📋',
      duration: 1500,
    });
  };

  // Generate initial palette
  useEffect(() => {
    if (activeTab === 'generator') {
      generateNewPalette();
    }
  }, [selectedModel, selectedHarmony, useAPI]);

  // Helper function to convert RGB to HSL
  const rgbToHsl = (r: number, g: number, b: number): string => {
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }

    return `hsl(${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%)`;
  };

  // Dynamic background colors based on palette with grid pattern
  const getBackgroundStyle = () => {
    if (palette.colors.length === 0) return {};
    
    const [primary, secondary, accent] = palette.colors;
    const gridSvg = `data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.04'%3E%3Cpath d='M0 0h30v30H0V0zm30 30h30v30H30V30z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E`;
    
    return {
      background: `linear-gradient(135deg, ${primary}08, ${secondary}08, ${accent}08), url("${gridSvg}")`,
      backgroundSize: 'cover, 60px 60px'
    };
  };

  const isPaletteFavorited = () => {
    if (palette.colors.length === 0) return false;
    const hexColors = palette.colors.map(color => hslToHex(color));
    return favoritePalettes.some(fav => 
      JSON.stringify(fav.colors) === JSON.stringify(hexColors)
    );
  };

  // Helper function to determine if text should be light or dark based on background
  const getContrastingTextColor = (backgroundColor: string) => {
    // Convert hex to RGB
    const hex = backgroundColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return black for light backgrounds, white for dark backgrounds
    return luminance > 0.5 ? '#000000' : '#ffffff';
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#363636',
            color: '#fff',
            borderRadius: '10px',
            fontSize: '14px',
          },
        }}
      />
      
      {/* Header - Fixed and Separated */}
      <header className="bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div 
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shadow-lg"
                style={{
                  background: palette.colors.length > 0 
                    ? `linear-gradient(135deg, ${palette.colors[0]}, ${palette.colors[2] || palette.colors[0]})` 
                    : 'linear-gradient(135deg, #3b82f6, #8b5cf6)'
                }}
              >
                <Palette className="text-white" size={16} />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  NocRenk
                </h1>
                <p className="text-xs text-gray-600 hidden sm:block">AI-powered color palettes</p>
              </div>
            </div>
            
            {/* Tab Navigation */}
            <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setActiveTab('generator')}
                className={`px-2 sm:px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-all flex items-center space-x-1 sm:space-x-2 ${
                  activeTab === 'generator' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <Zap size={14} />
                <span className="hidden sm:inline">Generator</span>
              </button>
              <button
                onClick={() => setActiveTab('extractor')}
                className={`px-2 sm:px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-all flex items-center space-x-1 sm:space-x-2 ${
                  activeTab === 'extractor' 
                    ? 'bg-white text-orange-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <ImageIcon size={14} />
                <span className="hidden sm:inline">Extract</span>
              </button>
              <button
                onClick={() => setActiveTab('favorites')}
                className={`px-2 sm:px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-all flex items-center space-x-1 sm:space-x-2 ${
                  activeTab === 'favorites' 
                    ? 'bg-white text-purple-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <Heart size={14} />
                <span className="hidden sm:inline">Favorites</span>
                <span className="text-xs bg-gray-200 text-gray-600 px-1 rounded">
                  {favoritePalettes.length}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('insights')}
                className={`px-2 sm:px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-all flex items-center space-x-1 sm:space-x-2 ${
                  activeTab === 'insights' 
                    ? 'bg-white text-green-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <Lightbulb size={14} />
                <span className="hidden sm:inline">Insights</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Flexible Body */}
      <main className="flex-1" style={getBackgroundStyle()}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {activeTab === 'generator' ? (
            <div className="py-6 sm:py-8 space-y-6 sm:space-y-8">
              {/* Generator Controls */}
              <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg p-4 sm:p-6">
                <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0 gap-4">
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
                      <button
                        onClick={() => setUseAPI(true)}
                        className={`px-2 sm:px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-all ${
                          useAPI 
                            ? 'bg-white text-blue-600 shadow-sm' 
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        <Zap size={12} className="inline mr-1" />
                        AI
                      </button>
                      <button
                        onClick={() => setUseAPI(false)}
                        className={`px-2 sm:px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-all ${
                          !useAPI 
                            ? 'bg-white text-blue-600 shadow-sm' 
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        Local
                      </button>
                    </div>

                    <button
                      onClick={() => setShowComponentPreview(!showComponentPreview)}
                      className={`px-2 sm:px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-all ${
                        showComponentPreview 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-600 hover:text-gray-800'
                      }`}
                    >
                      <span className="hidden sm:inline">Preview: </span>{showComponentPreview ? 'ON' : 'OFF'}
                    </button>

                    <button
                      onClick={() => setShowAccessibilityPanel(!showAccessibilityPanel)}
                      className={`px-2 sm:px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-all ${
                        showAccessibilityPanel 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'bg-gray-100 text-gray-600 hover:text-gray-800'
                      }`}
                    >
                      <Accessibility size={12} className="inline mr-1" />
                      <span className="hidden sm:inline">A11y</span>
                    </button>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={resetLocks}
                      className="px-2 sm:px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all duration-200 flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm"
                      disabled={!lockedColors.some(locked => locked)}
                    >
                      <RefreshCw size={12} />
                      <span className="hidden sm:inline">Reset</span>
                    </button>

                    <button
                      onClick={copyPaletteToClipboard}
                      className="px-2 sm:px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all duration-200 flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm"
                      disabled={palette.colors.length === 0}
                    >
                      <Copy size={12} />
                      <span className="hidden sm:inline">Copy</span>
                    </button>
                    
                    <button
                      onClick={savePaletteToFavorites}
                      className={`px-2 sm:px-3 py-2 rounded-lg transition-all duration-200 flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm ${
                        isPaletteFavorited()
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                      }`}
                      disabled={palette.colors.length === 0}
                    >
                      <Heart size={12} className={isPaletteFavorited() ? 'fill-current' : ''} />
                      <span className="hidden sm:inline">{isPaletteFavorited() ? 'Saved' : 'Save'}</span>
                    </button>
                    
                    <button
                      onClick={() => openExportModal()}
                      className="px-2 sm:px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all duration-200 flex items-center space-x-1 sm:space-x-2 shadow-md text-xs sm:text-sm"
                      disabled={palette.colors.length === 0}
                    >
                      <Download size={12} />
                      <span className="hidden sm:inline">Export</span>
                    </button>
                    
                    <button
                      onClick={generateNewPalette}
                      disabled={isGenerating}
                      className="px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 flex items-center space-x-1 sm:space-x-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg text-xs sm:text-sm"
                    >
                      <Shuffle className={`${isGenerating ? 'animate-spin' : ''}`} size={12} />
                      <span>{isGenerating ? 'Generating...' : 'Generate'}</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Accessibility Panel */}
              {showAccessibilityPanel && palette.colors.length > 0 && (
                <AccessibilityChecker palette={palette} />
              )}

              {/* Color Palette Display */}
              {palette.colors.length > 0 && (
                <section>
                  <div className="text-center mb-6">
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Your Color Palette</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto text-sm sm:text-base">
                      {useAPI ? (
                        <>Generated using AI trained on beautiful designs. Click colors to copy, use locks to preserve favorites.</>
                      ) : (
                        <>Created using {selectedHarmony.replace(/_/g, ' ')} color harmony. Click on any color to copy its hex value.</>
                      )}
                    </p>
                  </div>
                  <ColorPaletteDisplay 
                    palette={palette} 
                    onColorLock={useAPI ? handleColorLock : undefined}
                    lockedColors={lockedColors}
                  />
                </section>
              )}

              {/* Color Harmony Selector (Local Mode) */}
              {!useAPI && (
                <ColorHarmonySelector
                  selectedHarmony={selectedHarmony}
                  onHarmonyChange={setSelectedHarmony}
                  useAPI={useAPI}
                />
              )}

              {/* Model Selector (AI Mode) */}
              {useAPI && (
                <ModelSelector
                  selectedModel={selectedModel}
                  onModelChange={setSelectedModel}
                  isGenerating={isGenerating}
                  currentPalette={palette.colors}
                />
              )}

              {/* Component Showcase */}
              {showComponentPreview && palette.colors.length > 0 && (
                <section>
                  <div className="text-center mb-8">
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Live Component Preview</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto text-sm sm:text-base">
                      See your color palette in action across real UI components with both light and dark themes.
                    </p>
                  </div>
                  <ComponentShowcase palette={palette} />
                </section>
              )}
            </div>
          ) : activeTab === 'extractor' ? (
            /* Image Color Extractor Tab */
            <div className="py-6 sm:py-8">
              <div className="text-center mb-8">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Extract Colors from Images & Websites</h2>
                <p className="text-gray-600 max-w-2xl mx-auto text-sm sm:text-base">
                  Upload an image, provide a URL, or capture a website screenshot to automatically extract its dominant colors and create a beautiful palette.
                </p>
              </div>
              <ImageColorExtractor onPaletteExtracted={handlePaletteExtracted} />
            </div>
          ) : activeTab === 'insights' ? (
            /* Insights Tab */
            <div className="py-6 sm:py-8">
              <div className="text-center mb-8">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Color Insights & Analysis</h2>
                <p className="text-gray-600 max-w-2xl mx-auto text-sm sm:text-base">
                  Deep analysis of your color palette including accessibility, psychology, and design recommendations.
                </p>
              </div>
              {palette.colors.length > 0 ? (
                <ColorInsights palette={palette} />
              ) : (
                <div className="text-center py-16">
                  <Lightbulb className="mx-auto text-gray-300 mb-4" size={48} />
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">No Palette to Analyze</h3>
                  <p className="text-gray-500 mb-6">Generate a color palette first to see detailed insights and analysis.</p>
                  <button
                    onClick={() => setActiveTab('generator')}
                    className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 flex items-center space-x-2 mx-auto"
                  >
                    <Zap size={16} />
                    <span>Generate Palette</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* Favorites Tab */
            <div className="py-6 sm:py-8">
              <div className="text-center mb-8">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Favorite Color Palettes</h2>
                <p className="text-gray-600 max-w-2xl mx-auto text-sm sm:text-base">
                  Your saved color palettes. Click any palette to load it in the generator, or hover to export.
                </p>
              </div>

              {favoritePalettes.length === 0 ? (
                <div className="text-center py-16">
                  <Heart className="mx-auto text-gray-300 mb-4" size={48} />
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">No Favorite Palettes Yet</h3>
                  <p className="text-gray-500 mb-6">Generate some beautiful palettes and save them to see them here.</p>
                  <button
                    onClick={() => setActiveTab('generator')}
                    className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 flex items-center space-x-2 mx-auto"
                  >
                    <Zap size={16} />
                    <span>Start Generating</span>
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {favoritePalettes.map((favPalette) => (
                    <div
                      key={favPalette.id}
                      className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-200 cursor-pointer group"
                      onClick={() => loadFavoritePalette(favPalette)}
                    >
                      {/* Color Preview */}
                      <div className="h-24 flex relative">
                        {favPalette.colors.map((color, index) => (
                          <div
                            key={index}
                            className="flex-1"
                            style={{ backgroundColor: color }}
                          />
                        ))}
                        
                        {/* Hover Export Button */}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-all duration-300">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openExportModal(favPalette);
                              }}
                              className="w-10 h-10 bg-white bg-opacity-90 rounded-full flex items-center justify-center hover:bg-opacity-100 transition-all shadow-lg"
                              title="Export palette"
                            >
                              <Download size={16} className="text-gray-700" />
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Palette Info */}
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-gray-800 capitalize">
                            {favPalette.name}
                          </h3>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteFavoritePalette(favPalette.id!);
                            }}
                            className="opacity-0 group-hover:opacity-100 p-1 text-red-500 hover:text-red-700 transition-all"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">
                          {favPalette.model?.replace(/_/g, ' ') || 'Unknown'}
                        </p>
                        
                        {/* Improved Hex Code Display with Perfect Responsive Layout */}
                        <div className="grid grid-cols-5 gap-1 mb-3">
                          {favPalette.colors.map((color, index) => (
                            <span
                              key={index}
                              className="text-xs font-mono px-1 py-1 rounded border cursor-pointer hover:scale-105 transition-transform flex items-center justify-center text-center min-h-[24px] group/hex"
                              style={{ 
                                backgroundColor: color,
                                color: getContrastingTextColor(color),
                                borderColor: getContrastingTextColor(color) + '20'
                              }}
                              onClick={(e) => copyHexToClipboard(color, e)}
                              title={`Click to copy ${color}`}
                            >
                              <span className="truncate w-full text-center leading-tight">
                                {color}
                              </span>
                              <Copy size={8} className="opacity-0 group-hover/hex:opacity-60 transition-opacity ml-0.5 flex-shrink-0" />
                            </span>
                          ))}
                        </div>
                        
                        <p className="text-xs text-gray-400">
                          Saved {new Date(favPalette.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-800">
                Export Color Palette
                {exportingPalette && (
                  <span className="text-sm font-normal text-gray-600 ml-2">
                    ({exportingPalette.name})
                  </span>
                )}
              </h3>
              <button
                onClick={() => {
                  setShowExportModal(false);
                  setExportingPalette(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Enhanced Palette Preview - Works for both Generator and Favorites */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex h-8 rounded overflow-hidden mb-2">
                {exportingPalette ? (
                  // From favorites - use hex colors directly
                  exportingPalette.colors.map((color, index) => (
                    <div
                      key={index}
                      className="flex-1"
                      style={{ backgroundColor: color }}
                    />
                  ))
                ) : (
                  // From generator - convert HSL to hex
                  palette.colors.map((color, index) => (
                    <div
                      key={index}
                      className="flex-1"
                      style={{ backgroundColor: hslToHex(color) }}
                    />
                  ))
                )}
              </div>
              <div className="flex justify-between text-xs text-gray-600 font-mono">
                {exportingPalette ? (
                  // From favorites - show hex colors
                  exportingPalette.colors.map((color, index) => (
                    <span key={index}>{color}</span>
                  ))
                ) : (
                  // From generator - convert and show hex colors
                  palette.colors.map((color, index) => (
                    <span key={index}>{hslToHex(color)}</span>
                  ))
                )}
              </div>
              <div className="mt-2 text-center">
                <span className="text-xs text-gray-500">
                  {exportingPalette ? (
                    `From Favorites • ${exportingPalette.model?.replace(/_/g, ' ') || 'Custom'}`
                  ) : (
                    `Current Palette • ${useAPI ? selectedModel.replace(/_/g, ' ') : selectedHarmony.replace(/_/g, ' ')}`
                  )}
                </span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <button
                onClick={() => exportPalette('json')}
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
              >
                <Code className="text-blue-600 mb-2" size={24} />
                <h4 className="font-semibold text-gray-800">JSON</h4>
                <p className="text-sm text-gray-600">Standard format with metadata</p>
              </button>
              
              <button
                onClick={() => exportPalette('css')}
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
              >
                <FileText className="text-blue-600 mb-2" size={24} />
                <h4 className="font-semibold text-gray-800">CSS Variables</h4>
                <p className="text-sm text-gray-600">CSS custom properties</p>
              </button>
              
              <button
                onClick={() => exportPalette('scss')}
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
              >
                <FileText className="text-purple-600 mb-2" size={24} />
                <h4 className="font-semibold text-gray-800">SCSS Variables</h4>
                <p className="text-sm text-gray-600">Sass/SCSS format</p>
              </button>
              
              <button
                onClick={() => exportPalette('tailwind')}
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
              >
                <Layers className="text-cyan-600 mb-2" size={24} />
                <h4 className="font-semibold text-gray-800">Tailwind Config</h4>
                <p className="text-sm text-gray-600">Tailwind CSS theme</p>
              </button>
              
              <button
                onClick={() => exportPalette('figma')}
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
              >
                <Palette className="text-green-600 mb-2" size={24} />
                <h4 className="font-semibold text-gray-800">Figma Tokens</h4>
                <p className="text-sm text-gray-600">Design tokens format</p>
              </button>
              
              <button
                onClick={() => exportPalette('adobe')}
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
              >
                <Star className="text-red-600 mb-2" size={24} />
                <h4 className="font-semibold text-gray-800">Adobe Swatches</h4>
                <p className="text-sm text-gray-600">Adobe Creative Suite</p>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer - Fixed and Separated */}
      <footer className="bg-white/95 backdrop-blur-md border-t border-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Brand Section */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div 
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{
                    background: palette.colors.length > 0 
                      ? `linear-gradient(135deg, ${palette.colors[0]}, ${palette.colors[2] || palette.colors[0]})` 
                      : 'linear-gradient(135deg, #3b82f6, #8b5cf6)'
                  }}
                >
                  <Palette className="text-white" size={16} />
                </div>
                <h3 className="text-lg font-bold text-gray-800">NocRenk</h3>
              </div>
              <p className="text-gray-600 text-sm">
                AI-powered color palette generator for designers and developers. Create beautiful, harmonious color schemes with advanced AI models.
              </p>
            </div>

            {/* Features */}
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800">Features</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center space-x-2">
                  <Zap size={14} className="text-blue-500" />
                  <span>AI-Generated Palettes</span>
                </li>
                <li className="flex items-center space-x-2">
                  <ImageIcon size={14} className="text-orange-500" />
                  <span>Image & Website Color Extraction</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Heart size={14} className="text-red-500" />
                  <span>Save Favorites</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Download size={14} className="text-green-500" />
                  <span>Multiple Export Formats</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Layers size={14} className="text-purple-500" />
                  <span>Live Component Preview</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Accessibility size={14} className="text-orange-500" />
                  <span>Accessibility Checker</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Lightbulb size={14} className="text-yellow-500" />
                  <span>Color Insights & Analysis</span>
                </li>
              </ul>
            </div>

            {/* Copyright */}
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800">About</h4>
              <div className="text-sm text-gray-600 space-y-2">
                <p>© 2025 NocRenk. All rights reserved.</p>
                <p>
                  Created by{' '}
                  <a 
                    href="https://sinemarka.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 font-medium transition-colors"
                  >
                    Sinemarka
                  </a>
                </p>
                <p className="text-xs text-gray-500">
                  Powered by advanced AI color theory and machine learning algorithms
                </p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-200 mt-8 pt-6 text-center">
            <p className="text-xs text-gray-500">
              {useAPI ? 'Using Colormind.io API for AI-generated palettes' : `Using ${selectedHarmony.replace(/_/g, ' ')} color harmony algorithm`} • 
              Built with React, TypeScript, and Tailwind CSS
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;